import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Edit, Eye, Loader2, Plus, Save, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '../../../lib/dateFormat';

import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import LoeschenMitGrundDialog from '../../../components/ui/loeschen-mit-grund-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { FormLabel } from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import RichTextEditor, { type RichTextEditorHandle } from '../../../components/ui/rich-text-editor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';

import {
  api,
  emailTemplatesPreviewApi,
  emailTemplatesApi,
  type EmailTemplateDto,
  templateVariablesApi,
  type TemplateVariableMeta,
} from '../../../lib/api';
import type { VorschauDatensatz } from '../../../lib/api/templates';
import VorschauDatensatzWahl from './VorschauDatensatzWahl';

interface Standort {
  id: number;
  name: string;
  beschreibung?: string;
}

const extractPlaceholders = (s: string): string[] => {
  const out: string[] = [];
  const re = /\{\{([^}]+)\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s || ''))) {
    const v = (m[1] || '').trim();
    if (v && !out.includes(v)) out.push(v);
  }
  return out.sort();
};

const EmailTemplates = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogTab, setDialogTab] = useState<'edit' | 'preview'>('edit');

  const [templates, setTemplates] = useState<EmailTemplateDto[]>([]);
  const [zuLoeschen, setZuLoeschen] = useState<EmailTemplateDto | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplateDto | null>(null);

  const [templateSubject, setTemplateSubject] = useState('');
  const [templateContent, setTemplateContent] = useState(''); // html (optional <style>..</style> prefix)

  const [tenants, setTenants] = useState<Standort[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<{ id: number; name: string } | null>(null);

  // T-0179: Für welchen Bereich wird gepflegt — global (null) oder ein Standort.
  // Gespeichert wurde bisher immer global; die Standort-Auswahl wirkte nur auf
  // die Vorschau.
  const [geltung, setGeltung] = useState<number | null>(null);
  const [standortVorlagen, setStandortVorlagen] = useState<Record<string, EmailTemplateDto>>({});
  const [ebene, setEbene] = useState<'global' | 'standort' | 'standort_neu'>('global');
  const geltungName = tenants.find((t) => t.id === geltung)?.name ?? '';

  const [allowedVarsMeta, setAllowedVarsMeta] = useState<TemplateVariableMeta[]>([]);
  const mainEditorRef = useRef<RichTextEditorHandle | null>(null);

  // Preview (echte Daten, T-0180: Art je nach Vorlage)
  const [vorschauDatensatz, setVorschauDatensatz] = useState<VorschauDatensatz | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewSubject, setPreviewSubject] = useState<string | null>(null);
  const [previewSender, setPreviewSender] = useState<string>('');

  const createQuickTemplate = async (key: string) => {
    try {
      const exists = templates.some((t) => t.template_key === key);
      if (exists) {
        toast.info('Template existiert bereits');
        return;
      }
      const defaultSubject =
        key === 'communications.manual.course.participants'
          ? 'Information zu Ihrem Kurs'
          : key === 'communications.manual.trial_request'
            ? 'Probestunden-Anfrage – Rückmeldung'
            : 'Nachricht von {{schoolName}}';
      const defaultHtml =
        key === 'communications.manual.trial_request'
          ? '<p>Hallo {{studentName}},</p><p></p><p>Viele Grüße<br>{{schoolName}}</p>'
          : '<p>Hallo {{recipientName}},</p><p></p><p>Viele Grüße<br>{{schoolName}}</p>';

      const created = await emailTemplatesApi.create({ template_key: key, subject: defaultSubject, body_html: defaultHtml });
      setTemplates((prev) => [created, ...prev]);
      toast.success('Template angelegt');
    } catch (e: any) {
      toast.error(e?.message || 'Anlegen fehlgeschlagen');
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.get<Standort[]>('/api/standorte');
        setTenants(data);
        if (!selectedTenant && data.length > 0) {
          setSelectedTenant({ id: data[0].id, name: data[0].name });
        }
      } catch {
        // optional
      }
      try {
        const tpls = await emailTemplatesApi.list();
        setTemplates(tpls);
      } catch (e: any) {
        toast.error(e?.message || 'Fehler beim Laden der E-Mail-Vorlagen');
      }
    };
    void load();
  }, []);

  useEffect(() => {
    if (geltung === null) {
      setStandortVorlagen({});
      return;
    }
    const standort = tenants.find((t) => t.id === geltung);
    if (standort) setSelectedTenant({ id: standort.id, name: standort.name });
    let aktiv = true;
    emailTemplatesApi
      .list(geltung)
      .then((liste) => {
        if (aktiv) setStandortVorlagen(Object.fromEntries((liste || []).map((t) => [t.template_key, t])));
      })
      .catch((e: unknown) => {
        if (aktiv) toast.error(e instanceof Error ? e.message : 'Standort-Vorlagen konnten nicht geladen werden');
      });
    return () => {
      aktiv = false;
    };
  }, [geltung, tenants]);

  const filteredTemplates = templates.filter(
    (t) => t.template_key.toLowerCase().includes(searchQuery.toLowerCase()) || (t.subject || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const allowedVars = useMemo(() => allowedVarsMeta.map((v) => v.key), [allowedVarsMeta]);
  const usedVars = useMemo(() => {
    const all = [...extractPlaceholders(templateSubject), ...extractPlaceholders(templateContent)];
    return Array.from(new Set(all)).sort();
  }, [templateSubject, templateContent]);
  const unknownVars = useMemo(() => usedVars.filter((v) => !allowedVars.includes(v)), [usedVars, allowedVars]);

  const loadTemplateContext = async (templateKey: string) => {
    const [meta] = await Promise.all([templateVariablesApi.getMeta(templateKey), Promise.resolve(null)]);
    setAllowedVarsMeta(meta || []);
  };

  const handleEditTemplate = async (template: EmailTemplateDto) => {
    // Gibt es für den gewählten Standort eine eigene Vorlage, wird die bearbeitet.
    const eigene = geltung !== null ? standortVorlagen[template.template_key] : undefined;
    const quelle = eigene ?? template;
    setEbene(eigene ? 'standort' : 'global');
    setSelectedTemplate(template);
    setTemplateSubject(quelle.subject || '');
    setTemplateContent(quelle.body_html || '');
    setDialogTab('edit');
    setIsDialogOpen(true);

    try {
      // Welche Variablen es gibt, hängt am Vorlagenschlüssel, nicht am Standort.
      await loadTemplateContext(template.template_key);
    } catch {
      setAllowedVarsMeta([]);
    }
  };

  const handleCreateTemplate = async () => {
    setEbene('global');
    setSelectedTemplate(null);
    setTemplateSubject('');
    setTemplateContent('');
    // Ohne Schlüssel gibt es zumindest die Standardvariablen.
    try {
      await loadTemplateContext('');
    } catch {
      setAllowedVarsMeta([]);
    }
    setDialogTab('edit');
    setIsDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    setIsLoading(true);
    try {
      const currentBody = templateContent;
      if (!templateSubject.trim()) throw new Error('Betreff ist erforderlich');
      if (!currentBody.trim()) throw new Error('Inhalt ist erforderlich');

      if (selectedTemplate?.template_key && ebene !== 'global' && geltung !== null) {
        const key = selectedTemplate.template_key;
        const gespeichert = ebene === 'standort'
          ? await emailTemplatesApi.update(key, { subject: templateSubject, body_html: currentBody }, geltung)
          : await emailTemplatesApi.create({ template_key: key, subject: templateSubject, body_html: currentBody, standort_id: geltung });
        setStandortVorlagen((prev) => ({ ...prev, [key]: gespeichert }));
        toast.success(`Vorlage für ${geltungName} gespeichert`);
      } else if (selectedTemplate?.template_key) {
        const updated = await emailTemplatesApi.update(selectedTemplate.template_key, { subject: templateSubject, body_html: currentBody });
        setTemplates((prev) => prev.map((t) => (t.template_key === updated.template_key ? updated : t)));

        toast.success('Globale Vorlage gespeichert');
      } else {
        const key = prompt('Template-Key (z.B. trial.created.student):')?.trim();
        if (!key) throw new Error('Template-Key ist erforderlich');
        const created = await emailTemplatesApi.create({ template_key: key, subject: templateSubject, body_html: currentBody });
        setTemplates((prev) => [created, ...prev]);

        toast.success('E-Mail-Vorlage wurde erfolgreich angelegt');
      }

      setIsDialogOpen(false);
    } catch (e: any) {
      toast.error(e?.message || 'Fehler beim Speichern der E-Mail-Vorlage');
    } finally {
      setIsLoading(false);
    }
  };

  const renderPreview = async () => {
    if (!selectedTenant?.id) {
      toast.error('Bitte zuerst einen Standort auswählen');
      return;
    }
    if (!selectedTemplate?.template_key) {
      toast.error('Bitte Template zuerst speichern (Preview braucht einen Template-Key)');
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await emailTemplatesPreviewApi.render({
        template_key: selectedTemplate.template_key,
        standort_id: selectedTenant.id,
        // Ohne Datensatz zeigt die Vorschau Beispielwerte.
        ...(vorschauDatensatz ?? {}),
      });
      setPreviewHtml(res.html);
      setPreviewSubject(res.subject);
      setPreviewSender(
        [res.from_name, res.from_address ? `<${res.from_address}>` : ''].filter(Boolean).join(' '),
      );
    } catch (e: any) {
      toast.error(e?.message || 'Vorschau konnte nicht gerendert werden');
      setPreviewHtml(null);
      setPreviewSubject(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const insertToken = (token: string) => {
    const normalized = String(token || '').trim();
    if (!normalized) return;
    if (mainEditorRef.current) {
      mainEditorRef.current.insertToken(normalized);
      return;
    }
    setTemplateContent((prev) => (prev || '') + normalized);
  };

  return <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold leading-7">E-Mail-Vorlagen</h1>
          <p className="text-muted-foreground">Verwalten Sie E-Mail-Vorlagen für verschiedene Prozesse</p>
        </div>
        <Button onClick={() => void handleCreateTemplate()}><Plus className="mr-2 h-4 w-4" />Neue Vorlage</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Quick Create: Nachrichten-Modal</CardTitle>
          <CardDescription>Legt Standard-Templates für das universelle Nachrichten-Modal an (falls noch nicht vorhanden).</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => void createQuickTemplate('communications.manual.person')}>
            communications.manual.person
          </Button>
          <Button variant="outline" onClick={() => void createQuickTemplate('communications.manual.course.participants')}>
            communications.manual.course.participants
          </Button>
          <Button variant="outline" onClick={() => void createQuickTemplate('communications.manual.trial_request')}>
            communications.manual.trial_request
          </Button>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        <div className="md:w-72">
          <Select
            value={geltung === null ? 'global' : String(geltung)}
            onValueChange={(wert) => setGeltung(wert === 'global' ? null : Number(wert))}
          >
            <SelectTrigger size="md" aria-label="Vorlagen für">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="global">Global (alle Standorte)</SelectItem>
              {tenants.map((t) => (
                <SelectItem key={t.id} value={String(t.id)}>Standort {t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input uiSize="md" placeholder="Vorlagen durchsuchen..." className="pl-8" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>E-Mail-Vorlagen</CardTitle>
          <CardDescription>Eine Liste aller verfügbaren E-Mail-Vorlagen</CardDescription>
        </CardHeader>
        <CardContent>
          <Table density="compact">
            <TableHeader>
              <TableRow>
                <TableHead density="compact">Key</TableHead>
                <TableHead density="compact">Betreff</TableHead>
                <TableHead density="compact">Status</TableHead>
                {geltung !== null ? <TableHead density="compact">Gilt in {geltungName}</TableHead> : null}
                <TableHead density="compact">Zuletzt bearbeitet</TableHead>
                <TableHead density="compact" className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTemplates.map((template) => <TableRow key={template.template_key}>
                  <TableCell density="compact" className="font-medium">{template.template_key}</TableCell>
                  <TableCell density="compact">{template.subject}</TableCell>
                  <TableCell density="compact">
                    <Badge size="sm" variant={Number(template.is_active) ? 'success' : 'secondary'}>{Number(template.is_active) ? 'aktiv' : 'inaktiv'}</Badge>
                  </TableCell>
                  {geltung !== null ? (
                    <TableCell density="compact">
                      {standortVorlagen[template.template_key] ? (
                        <Badge size="sm" variant="default">eigene Vorlage</Badge>
                      ) : (
                        <Badge size="sm" variant="secondary">globale Vorlage</Badge>
                      )}
                    </TableCell>
                  ) : null}
                  <TableCell density="compact">{formatDateTime(template.updated_at)}</TableCell>
                  <TableCell density="compact" className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => void handleEditTemplate(template)}>
                      <Edit className="h-4 w-4" />
                      <span className="sr-only">Bearbeiten</span>
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setZuLoeschen(template)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                      <span className="sr-only">Löschen</span>
                    </Button>
                  </TableCell>
                </TableRow>)}
              {filteredTemplates.length === 0 && <TableRow>
                  <TableCell density="compact" colSpan={geltung !== null ? 6 : 5} className="h-24 text-center">
                    Keine Vorlagen gefunden.
                  </TableCell>
                </TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>{selectedTemplate ? 'E-Mail-Vorlage bearbeiten' : 'Neue E-Mail-Vorlage erstellen'}</DialogTitle>
            <DialogDescription>HTML-Inhalte für den Mittelteil bearbeiten und Variablen per Klick einfügen. Header/Footer kommen zentral aus dem Wrapper.</DialogDescription>
          </DialogHeader>

          <Tabs value={dialogTab} onValueChange={(v) => setDialogTab(v as any)}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="edit">
                <Edit className="mr-2 h-4 w-4" />
                Bearbeiten
              </TabsTrigger>
              <TabsTrigger value="preview">
                <Eye className="mr-2 h-4 w-4" />
                Vorschau
              </TabsTrigger>
            </TabsList>

            <TabsContent value="edit" className="space-y-4">
              <div className="grid gap-4 py-4">
                {selectedTemplate ? (
                  <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
                    {geltung === null ? (
                      <p>Globale Vorlage — gilt an allen Standorten ohne eigene Vorlage.</p>
                    ) : ebene === 'standort' ? (
                      <p>Eigene Vorlage von <strong>{geltungName}</strong> — sie gilt dort vor der globalen.</p>
                    ) : ebene === 'standort_neu' ? (
                      <p>
                        Neue Vorlage nur für <strong>{geltungName}</strong>. Sie entsteht beim Speichern; die globale
                        bleibt unverändert.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        <p>
                          In <strong>{geltungName}</strong> gilt die globale Vorlage. Was du hier speicherst, ändert sie
                          für alle Standorte ohne eigene Vorlage.
                        </p>
                        <Button type="button" size="sm" variant="outline" onClick={() => setEbene('standort_neu')}>
                          Eigene Vorlage für {geltungName} anlegen
                        </Button>
                      </div>
                    )}
                  </div>
                ) : null}
                <div className="space-y-2">
                  <FormLabel>Standort für Vorschau und Variablen</FormLabel>
                  <Select value={selectedTenant?.id?.toString() || ''} onValueChange={async (value) => {
                    const t = tenants.find((x) => x.id.toString() === value);
                    if (!t) return;
                    setSelectedTenant({ id: t.id, name: t.name });
                    try {
                      if (selectedTemplate?.template_key) {
                        await loadTemplateContext(selectedTemplate.template_key);
                      }
                    } catch {
                      // ignore
                    }
                  }}>
                    <SelectTrigger size="md">
                      <SelectValue placeholder="Standort auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {tenants.map((t) => <SelectItem key={t.id} value={t.id.toString()}>
                          {t.name}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <FormLabel htmlFor="template-subject">Betreff</FormLabel>
                  <Input id="template-subject" uiSize="md" value={templateSubject} onChange={(e) => setTemplateSubject(e.target.value)} placeholder="Betreff der E-Mail" />
                </div>

                <div className="space-y-2">
                  <FormLabel>Inhalt (HTML)</FormLabel>
                  <RichTextEditor
                    ref={mainEditorRef}
                    value={templateContent}
                    onChange={setTemplateContent}
                    className="min-h-[380px]"
                    placeholder="<p>Hallo {{studentName}},</p>"
                  />
                </div>

                <div className="rounded-md border p-3 text-sm">
                  <div className="mb-2 font-medium">Variablen</div>
                  <div className="mb-2 text-xs text-muted-foreground">
                    Klicken, um die Variable an der Cursorposition einzufügen. Beim Versand stehen
                    hier die echten Werte; was hier fehlt, bleibt in der Mail leer.
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {allowedVarsMeta.length === 0 ? (
                      <span className="text-xs text-muted-foreground">Keine Variablen definiert</span>
                    ) : (
                      allowedVarsMeta.map((v) => <button
                          key={v.key}
                          type="button"
                          onClick={() => insertToken(`{{${v.key}}}`)}
                          className="cursor-pointer"
                          title={v.example ? `${v.label} — z. B. ${v.example}` : v.label}
                        >
                          <Badge size="sm" variant={usedVars.includes(v.key) ? 'default' : 'secondary'}>
                            {v.label}
                            <span className="ml-1 opacity-60">{`{{${v.key}}}`}</span>
                          </Badge>
                        </button>)
                    )}
                  </div>
                  {unknownVars.length > 0 ? (
                    <div className="mt-3 text-xs text-destructive">Unbekannte Variablen im Template: {unknownVars.map((v) => `{{${v}}}`).join(', ')}</div>
                  ) : null}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="preview" className="space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{previewSubject ?? templateSubject ?? '(kein Betreff)'}</CardTitle>
                  <CardDescription>
                    Von: {previewSender || 'Absender aus den E-Mail-Einstellungen'}
                    <br />
                    An: Beispiel &lt;example@example.com&gt;
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <VorschauDatensatzWahl
                      templateKey={selectedTemplate?.template_key ?? ''}
                      standortId={selectedTenant?.id}
                      value={vorschauDatensatz}
                      onChange={setVorschauDatensatz}
                    />

                    <div className="flex items-center gap-2">
                      <Button onClick={() => void renderPreview()} disabled={previewLoading || !selectedTemplate?.template_key}>
                        {previewLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />}
                        Vorschau rendern
                      </Button>
                      {!selectedTemplate?.template_key ? (
                        <span className="text-xs text-muted-foreground">Hinweis: Preview benötigt einen gespeicherten Template-Key.</span>
                      ) : null}
                    </div>

                    {previewHtml ? (
                      <div className="rounded-md border overflow-hidden">
                        <iframe
                          title="E-Mail Vorschau"
                          sandbox=""
                          srcDoc={previewHtml}
                          style={{ width: '100%', height: 700, border: 0 }}
                        />
                      </div>
                    ) : (
                      <div className="rounded-md border p-4 text-sm text-muted-foreground">
                        „Vorschau rendern“ zeigt die Mail mit Kopf und Fußleiste, so wie sie beim
                        Empfänger ankommt. Ohne ausgewählten Datensatz stehen Beispielwerte in den
                        Platzhaltern.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveTemplate} disabled={isLoading}>
              {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LoeschenMitGrundDialog
        offen={zuLoeschen !== null}
        titel={
          geltung !== null && zuLoeschen && standortVorlagen[zuLoeschen.template_key]
            ? `Eigene Vorlage von ${geltungName} entfernen?`
            : `Vorlage „${zuLoeschen?.template_key ?? ''}“ deaktivieren?`
        }
        beschreibung={
          geltung !== null && zuLoeschen && standortVorlagen[zuLoeschen.template_key]
            ? 'Danach gilt an diesem Standort wieder die globale Vorlage. Der Grund wird im Protokoll festgehalten.'
            : 'Die Vorlage wird deaktiviert und steht danach nicht mehr zur Auswahl. Der Grund wird im Protokoll festgehalten.'
        }
        onAbbrechen={() => setZuLoeschen(null)}
        onLoeschen={async (grund) => {
          if (!zuLoeschen) return;
          const key = zuLoeschen.template_key;
          if (geltung !== null && standortVorlagen[key]) {
            await emailTemplatesApi.delete(key, grund, geltung);
            setStandortVorlagen((prev) => {
              const rest = { ...prev };
              delete rest[key];
              return rest;
            });
            setZuLoeschen(null);
            toast.success(`Eigene Vorlage von ${geltungName} entfernt`);
            return;
          }
          await emailTemplatesApi.delete(key, grund);
          setTemplates((prev) => prev.map((t) => (t.template_key === key ? { ...t, is_active: 0 } : t)));
          setZuLoeschen(null);
          toast.success('Vorlage wurde deaktiviert');
        }}
      />
    </div>;
};

export default EmailTemplates;